/**
 * Disk template.
 */
import DiskCapacityBar from '@components/atoms/container/diskCapacityBar';
import DiskCapacityBarInfo from '@components/atoms/text/diskCapacityBarInfo';
import DiskCapacityBarMaxCapacity from '@components/atoms/text/diskCapacityBarMaxCapacity';
import DiskCapacityCloud from '@components/atoms/images/diskCapacityCloud';
import DiskCapacityContainer from '@components/atoms/container/diskCapacityContainer';
import DiskCapacityInfo from '@components/atoms/text/diskCapacityInfo';
import DiskCapacityInnerBar from '@components/atoms/container/diskCapacityInnerBar';
import DiskCapacityOuterBar from '@components/atoms/container/diskCapacityOuterBar';
import DiskCapacityTitle from '@components/atoms/text/diskCapacityTitle';
import Focus from '@shared/types/enums/focus';
import MainContainer from '@components/atoms/container/contentContainer';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * Constants
 */
const LITERALS = 'PAGES.SETTINGS.DISK';

/**
 * Props interface for the Disk template component.
 */
interface Props {
  days: string;
  quota: number;
  used: number;
}

/**
 * Disk template component.
 */
export default function DiskTemplate(parentProps: Props) {
  const { callbacks, literals, props } = useDiskTemplate(parentProps);

  return (
    <MainContainer id={'diskContainer'} {...callbacks.mainContainer}>
      <DiskCapacityContainer>
        <DiskCapacityTitle>
          {TranslationManager.get(`${LITERALS}.TITLE`)}
        </DiskCapacityTitle>
        <DiskCapacityInfo>
          {literals.info}
          <br></br>
          <br></br>
          {literals.detail}
        </DiskCapacityInfo>
        <DiskCapacityBar>
          <DiskCapacityCloud />
          <DiskCapacityOuterBar>
            <DiskCapacityInnerBar {...props.diskCapacityInnerBar} />
            <DiskCapacityBarInfo id={'diskCapacityBarInfo'}>
              {literals.barInfo}
            </DiskCapacityBarInfo>
          </DiskCapacityOuterBar>
          <DiskCapacityBarMaxCapacity id={'diskCapacityBarMaxCapacity'}>
            {literals.maxCapacity}
          </DiskCapacityBarMaxCapacity>
        </DiskCapacityBar>
      </DiskCapacityContainer>
    </MainContainer>
  );
}

/**
 * Hook to use the disk template.
 * Handles the disk template logic.
 */
function useDiskTemplate(parentProps: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.DISK));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.DISK));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Disk keyboard
  useKeyboard({
    focus: Focus.DISK,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // Menu keyboard
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.DISK));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const { days, quota, used } = parentProps;
    return {
      callbacks: {
        mainContainer: {
          onMouseEnter,
        },
      },
      literals: {
        title: TranslationManager.get(`${LITERALS}.TITLE`),
        info: TranslationManager.get(`${LITERALS}.INFO`),
        detail: TranslationManager.get(`${LITERALS}.DETAIL`, days),
        barInfo: TranslationManager.get(
          `${LITERALS}.BAR_INFO`,
          `${used || '  '}`
        ),
        maxCapacity: TranslationManager.get(
          `${LITERALS}.MAX_CAPACITY`,
          `${quota || '  '}`
        ),
      },
      props: {
        diskCapacityInnerBar: {
          animation: { transition: 'width 0.3s' },
          width: (used && quota && used / quota) || 0,
        },
      },
    };
  }, [parentProps]);
}
